import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

import { useCalculationStore } from '@/stores/calculation'
import { type ActionPlanInput, type CalculationInput, type ContractType, contractHasEndDate } from '@/types/calculation'

/** The form fields that can carry a validation message. */
export type InputField = 'electricityUsage' | 'generation' | 'gasUsage' | 'contractType' | 'contractEndDate'

/**
 * Which field a message from capi belongs to. The gas checkbox sits under the gas field and the
 * "weet ik niet" checkbox under the date, so their messages land there too.
 */
const SERVER_FIELDS: Record<string, InputField> = {
  electricity_usage: 'electricityUsage',
  generation: 'generation',
  gas_usage: 'gasUsage',
  has_gas_connection: 'gasUsage',
  contract_type: 'contractType',
  end_date: 'contractEndDate',
  contract_end_unknown: 'contractEndDate',
}

/**
 * What the visitor filled in. A store, so the estimation modal (#6316) writes into the same fields
 * the input step reads, and "Opnieuw berekenen" (#6318) brings the values back.
 *
 * The numbers stay strings here: that is what the inputs produce, and an empty field is then simply
 * an empty string instead of a sentinel value. toCalculationInput converts.
 */
export const useInputStore = defineStore('input', () => {
  const electricityUsage = ref('')
  const generation = ref('')
  const hasGasConnection = ref(true)
  const gasUsage = ref('')
  const contractType = ref<ContractType | ''>('')
  const contractEndDate = ref('')
  const contractEndUnknown = ref(false)

  // What the estimation modal asked, starting on the design's defaults: an average household with
  // a common roof. In the store, so reopening the modal picks up where the visitor left off
  // instead of resetting (#6331).
  const estimationPersons = ref(2)
  const estimationPanels = ref(10)

  /** Filled from a rejected calculation, so nothing turns red while the visitor is still typing. */
  const fieldErrors = ref<Partial<Record<InputField, string>>>({})

  /** Messages capi returned for something the form does not show as a field of its own. */
  const generalErrors = ref<string[]>([])

  /**
   * Places the messages capi returned (#6322) next to the fields they belong to. The tool does no
   * checking of its own: one set of rules, one set of wording, and bounds like the bulk limits
   * cannot drift apart from the service that enforces them.
   *
   * Anything the form has no field for stays behind as a general message above the button.
   */
  function applyServerErrors(errors: Record<string, string>): void {
    const found: typeof fieldErrors.value = {}
    const rest: string[] = []

    for (const [key, message] of Object.entries(errors)) {
      const field = SERVER_FIELDS[key]

      if (field === undefined) {
        rest.push(message)
      } else if (found[field] === undefined) {
        found[field] = message
      }
    }

    fieldErrors.value = found
    generalErrors.value = rest
  }

  function clearErrors(): void {
    fieldErrors.value = {}
    generalErrors.value = []
  }

  /**
   * The end date travels along even though the calculation does not use it: capi validates it, so
   * its message comes from the same place as the others. The advice later in the flow does use it.
   */
  function toCalculationInput(): CalculationInput {
    return {
      electricity_usage: toNumber(electricityUsage.value),
      generation: toNumber(generation.value),
      has_gas_connection: hasGasConnection.value,
      gas_usage: hasGasConnection.value ? toNumber(gasUsage.value) : null,
      contract_type: contractType.value,
      end_date: contractEndUnknown.value || contractEndDate.value === '' ? null : contractEndDate.value,
      contract_end_unknown: contractEndUnknown.value,
    }
  }

  /**
   * The action plan payload (#6319): the same fields the calculation takes, plus contact details.
   */
  function toActionPlanInput(email: string, newsletterConsent: boolean): ActionPlanInput {
    return {
      ...toCalculationInput(),
      email,
      newsletter_consent: newsletterConsent,
    }
  }

  // Without a gas connection there is nothing to fill in: the field empties along with the
  // checkbox, so no stale number lingers behind the disabled field.
  watch(hasGasConnection, (has) => {
    if (!has) {
      gasUsage.value = ''
    }
  })

  // A variable, hybrid or dynamic contract has no end date: choosing one ticks "niet van
  // toepassing" itself, which greys out the date field through the existing checkbox mechanism and
  // sends capi the combination it accepts. Back to a fixed contract, the question is open again.
  watch(contractType, (type) => {
    const applicable = contractHasEndDate(type)

    contractEndUnknown.value = !applicable

    if (!applicable) {
      contractEndDate.value = ''
    }
  })

  // Editing a field answers its message, so the message leaves with the edit. The checkboxes clear
  // the field they replace.
  watch(electricityUsage, () => delete fieldErrors.value.electricityUsage)
  watch(generation, () => delete fieldErrors.value.generation)
  watch([gasUsage, hasGasConnection], () => delete fieldErrors.value.gasUsage)
  watch(contractType, () => delete fieldErrors.value.contractType)
  watch([contractEndDate, contractEndUnknown], () => delete fieldErrors.value.contractEndDate)

  // The messages came with the submitted combination, so an edit answers the whole rejection. The
  // same for a failed attempt: editing replaces it. A finished result stays.
  const calculation = useCalculationStore()

  watch(
    [electricityUsage, generation, hasGasConnection, gasUsage, contractType, contractEndDate, contractEndUnknown],
    () => {
      if (calculation.status === 'invalid' || calculation.status === 'failed') {
        calculation.reset()
        generalErrors.value = []
      }
    },
  )

  return {
    electricityUsage,
    generation,
    hasGasConnection,
    gasUsage,
    contractType,
    contractEndDate,
    contractEndUnknown,
    estimationPersons,
    estimationPanels,
    fieldErrors,
    generalErrors,
    applyServerErrors,
    clearErrors,
    toCalculationInput,
    toActionPlanInput,
  }
})

/** Empty stays empty: capi has to see a field the visitor skipped as missing, not as a zero. */
function toNumber(value: string): number | null {
  return value === '' ? null : Number(value)
}
